import React, { useEffect } from 'react';
import NameGuess from '../components/nameGuess';
import { Backdrop, CircularProgress, Container, Alert } from '@mui/material';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useSearchParams } from 'react-router-dom';
import OAuthCallback from '../components/OAuthCallback';
import { useAuthGen2 } from '../hooks/useAuthGen2';
import FacebookBrowserWarning from '../components/FacebookBrowserWarning';

const Home: React.FC = () => {
    // Remove duplicate game state - NameGuess handles this via GameManager
    const [searchParams, setSearchParams] = useSearchParams();
    useAuthenticator((context) => [context.user]);
    const { isAuthenticated, token, loading: authLoading } = useAuthGen2();

    // Check if this is an OAuth callback
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const authError = searchParams.get('auth_error');
    const isOAuthCallback = !!(code && state);

    // Removed duplicate game loading - NameGuess handles this via GameManager

    // Always call hooks in the same order, regardless of conditions
    useEffect(() => {
        // Clear auth error from URL after showing it
        if (authError) {
            const timer = setTimeout(() => {
                setSearchParams({});
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [authError, setSearchParams]);

    // Clean up Facebook tracking parameters (fbclid, etc.) from URL
    useEffect(() => {
        const fbclid = searchParams.get('fbclid');
        const fbAsyncInit = searchParams.get('fbAsyncInit');

        if (fbclid || fbAsyncInit) {
            // Remove Facebook tracking parameters without affecting other params
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('fbclid');
            newParams.delete('fbAsyncInit');

            // Only update if we're not in OAuth callback mode
            if (!isOAuthCallback) {
                window.history.replaceState({}, '', `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`);
            }
        }
    }, [searchParams, isOAuthCallback]);


    // Render OAuth callback component if needed
    if (isOAuthCallback) {
        return <OAuthCallback />;
    }

    return (
        <Container sx={{ px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
            {/* SEO-optimized heading - accessible to search engines and screen readers */}
            <h2 className="sr-only">Famous Initials - Daily Word Game to Guess Celebrities by Their Initials</h2>

            {/* SEO-optimized description for no-JS browsers */}
            <noscript>
                <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f5f5f5', margin: '20px 0' }}>
                    <p>
                        Welcome to Famous Initials, a fun daily word game where you guess the names of famous people
                        based on their initials. Test your knowledge of celebrities, historical figures, athletes,
                        and pop culture icons in this challenging trivia game. Play for free every day!
                    </p>
                </div>
            </noscript>

            {/* Facebook browser warning */}
            <FacebookBrowserWarning />

            {authError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Authentication failed: {authError}
                </Alert>
            )}
            {!authLoading ?
                <NameGuess />
            : <Backdrop
                sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                open={authLoading}
                >
                <CircularProgress color="inherit" />
            </Backdrop> }
        </Container>
    );

    // Removed loadGame function - NameGuess handles game loading via GameManager
};
export default Home;
